const STORAGE_PREFIX = 'whatquiz.chat-handoff.';
const CLASS_PHOTO_ACTIVATION_KEY = 'whatquiz.class-photo-activation';
const HANDOFF_TTL_MS = 10 * 60 * 1000;
const MAX_HANDOFF_TTL_MS = 30 * 60 * 1000;
const MAX_CONTEXT_LENGTH = 6000;
const MAX_REFERENCE_URL_LENGTH = 4096;
// 한글 파일 이벤트 문서 ID 계약(backend DOC_ID_PATTERN 과 동일: hwpdoc_ + hex 32). 다른 형식은 첨부하지 않는다.
const SOURCE_DOC_ID_PATTERN = /^hwpdoc_[0-9a-f]{32}$/;
const MAX_EDIT_SUGGESTIONS = 3;
const TRUSTED_REFERENCE_HOSTS = new Set([
  'firebasestorage.googleapis.com',
  'storage.googleapis.com',
]);

export const CHAT_HANDOFF_TARGETS = Object.freeze({
  ppt_maker: {
    label: 'PPT',
    instruction: '다음 내용을 바탕으로 선생님이 바로 활용할 수 있는 PPT를 만들어주세요.',
  },
  worksheet_gen: {
    label: '학습지',
    instruction: '다음 내용을 바탕으로 학생에게 바로 배부할 수 있는 학습지나 활동지를 만들어주세요.',
  },
  image_generation: {
    label: '이미지',
    instruction: '다음 내용의 핵심이 잘 드러나도록 깔끔하고 보기 좋은 교육용 이미지로 만들어주세요.',
  },
  // 한글 파일 이벤트(시험지·안내문) → /chat AI 한글에서 방금 만든 HWPX 를 첨부한 채 이어서 편집
  document: {
    label: 'AI 한글',
    instruction: '첨부한 한글 파일을 바탕으로 요청한 부분만 수정해 다시 한글 파일로 만들어주세요.',
    // 채팅 답변 카드 → 선생님이 새 탭에서 자기 양식을 첨부해 칸만 채운다.
    formInstruction: '첨부한 한글 양식의 표와 칸 구성은 그대로 두고, 아래 내용을 알맞은 칸에 채워 한글 파일로 만들어주세요.',
  },
  // 한글 파일 이벤트(안내문·생기부) → /chat 이어가기
  admin_docs: {
    label: '행정문서',
    instruction: '다음 내용을 바탕으로 학교에서 바로 쓸 수 있는 안내문·행정 문서를 한글 파일로 다시 만들어주세요.',
  },
  record: {
    label: '생기부',
    instruction: '다음 관찰 내용을 바탕으로 학교생활기록부 문장을 다듬어 한글 파일로 만들어주세요. 실명·학번은 넣지 마세요.',
  },
});

// 채팅 답변의 "내 양식 채우기" 카드. 다른 document 이어가기(한글 이벤트·학급사진)와 구분한다.
export const CHAT_HANDOFF_FORM_FILL_PURPOSE = 'formFill';

const normalizeContext = (value) => String(value || '')
  .replace(/```\s*file[-_ ]?artifact\s*\n[\s\S]*?\n```/gi, '')
  .replace(/```\s*hwpx[-_ ]?edit\s*\n[\s\S]*?\n```/gi, '')
  .split('\n')
  .filter((line) => !/\/chat\?category=(ppt_maker|worksheet_gen|image_generation|document)/.test(line))
  .join('\n')
  .replace(/\n{3,}/g, '\n\n')
  .trim()
  .slice(0, MAX_CONTEXT_LENGTH);

const normalizeLabel = (value, maxLength) => Array.from(String(value || ''))
  .map((character) => {
    const code = character.charCodeAt(0);
    return code < 32 || code === 127 ? ' ' : character;
  })
  .join('')
  .replace(/\s+/g, ' ')
  .trim()
  .slice(0, maxLength);

const normalizeReferenceImageUrl = (value) => {
  const candidate = String(value || '').trim();
  if (!candidate || candidate.length > MAX_REFERENCE_URL_LENGTH) return '';
  try {
    const url = new URL(candidate);
    if (url.protocol !== 'https:') return '';
    const host = url.hostname.toLowerCase();
    if (
      !TRUSTED_REFERENCE_HOSTS.has(host)
      && !host.endsWith('.firebasestorage.app')
    ) {
      return '';
    }
    return url.toString();
  } catch (_) {
    return '';
  }
};

const normalizeSourceDocId = (value) => {
  const candidate = String(value || '').trim();
  return SOURCE_DOC_ID_PATTERN.test(candidate) ? candidate : '';
};

const normalizeEditSuggestions = (value) => (
  Array.isArray(value)
    ? value
      .map((item) => normalizeLabel(item, 80))
      .filter(Boolean)
      .slice(0, MAX_EDIT_SUGGESTIONS)
    : []
);

/**
 * v3 첨부 메타(sourceDoc*)를 정규화한다. sourceDocId 가 hwpdoc_ 형식이 아니면 전부 버린다.
 * 반환값은 payload 에 spread 할 수 있는 객체(없으면 빈 객체).
 */
const normalizeSourceDocFields = ({
  editSuggestions,
  sourceDocFilename,
  sourceDocId,
  sourceDocTitle,
}) => {
  const docId = normalizeSourceDocId(sourceDocId);
  if (!docId) return {};
  const filename = normalizeLabel(sourceDocFilename, 160);
  return {
    sourceDocId: docId,
    sourceDocTitle: normalizeLabel(sourceDocTitle, 120) || '한글 문서',
    sourceDocFilename: /\.hwpx?$/i.test(filename) ? filename : `${filename || '한글 문서'}.hwpx`,
    editSuggestions: normalizeEditSuggestions(editSuggestions),
  };
};

export const getChatHandoffTarget = (href) => {
  if (!href || typeof href !== 'string') return null;
  try {
    const url = new URL(href, 'https://whatquiz.local');
    if (url.origin !== 'https://whatquiz.local' || url.pathname !== '/chat') {
      return null;
    }
    const category = url.searchParams.get('category');
    return CHAT_HANDOFF_TARGETS[category] ? category : null;
  } catch (_) {
    return null;
  }
};

// 답변 끝 CTA 카드로 올릴 카테고리만. document/admin_docs/record 는 텍스트 링크로 둔다.
//   이 목록의 힌트 줄은 ProAIMessage 가 본문에서 지우므로, 보여야 하는 "양식 채우기" 링크(document)는 넣지 않는다.
export const CHAT_HANDOFF_SUGGESTION_CATEGORIES = Object.freeze([
  'ppt_maker',
  'worksheet_gen',
  'image_generation',
]);

const HANDOFF_SUGGESTION_CATEGORY_SET = new Set(CHAT_HANDOFF_SUGGESTION_CATEGORIES);

// 제목 + 버튼만. 설명 줄은 두지 않는다(답변 아래 글이 이미 많다 — 2026-09 사장 결정).
export const HANDOFF_SUGGESTION_COPY = Object.freeze({
  ppt_maker: {
    title: '이 내용으로 PPT를 바로 만들 수 있어요',
    primaryLabel: 'PPT 만들기로 이어가기',
  },
  worksheet_gen: {
    title: '이 내용으로 학습지를 바로 만들 수 있어요',
    primaryLabel: '학습지 만들기로 이어가기',
  },
  image_generation: {
    title: '이 내용으로 교육용 이미지를 만들 수 있어요',
    primaryLabel: '이미지 만들기로 이어가기',
  },
});

const MARKDOWN_LINK_PATTERN = '\\[([^\\]]+)\\]\\(([^)]+)\\)';
const HINT_LINE_PREFIX_RE = /^(?:>|💡|[-*])\s*/;
const HINT_VISIBLE_MAX_CHARS = 80;

const isSuggestionHandoffHref = (href) => {
  const target = getChatHandoffTarget(href);
  return target && HANDOFF_SUGGESTION_CATEGORY_SET.has(target) ? target : null;
};

const iterMarkdownLinks = (text) => {
  const matches = [];
  const source = String(text || '');
  const linkRe = new RegExp(MARKDOWN_LINK_PATTERN, 'g');
  let match = linkRe.exec(source);
  while (match) {
    matches.push({
      label: match[1],
      href: match[2],
      index: match.index,
    });
    match = linkRe.exec(source);
  }
  return matches;
};

const stripHintPrefixes = (line) => {
  let result = String(line || '').trim();
  while (HINT_LINE_PREFIX_RE.test(result)) {
    result = result.replace(HINT_LINE_PREFIX_RE, '');
  }
  return result;
};

const isHandoffHintLine = (line) => {
  const links = iterMarkdownLinks(line);
  if (links.length !== 1) return false;
  if (!isSuggestionHandoffHref(links[0].href)) return false;
  const visible = stripHintPrefixes(line).replace(new RegExp(MARKDOWN_LINK_PATTERN, 'g'), '$1');
  return visible.length > 0 && visible.length <= HINT_VISIBLE_MAX_CHARS;
};

/**
 * 답변에서 카테고리 이어가기 마크다운 링크를 찾는다.
 * 같은 대상은 첫 등장만 남기고, 카드는 최대 1개다.
 */
export const extractHandoffSuggestionsFromContent = (content) => {
  const lines = String(content || '').split(/\r?\n/);
  const seen = new Set();
  const results = [];
  for (let lineIndex = 0; lineIndex < lines.length; lineIndex += 1) {
    const links = iterMarkdownLinks(lines[lineIndex]);
    for (let i = 0; i < links.length; i += 1) {
      const targetCategory = isSuggestionHandoffHref(links[i].href);
      if (!targetCategory || seen.has(targetCategory)) continue;
      seen.add(targetCategory);
      results.push({
        targetCategory,
        href: links[i].href,
        label: links[i].label,
        lineIndex,
      });
      if (results.length >= 1) return results;
    }
  }
  return results;
};

/**
 * 힌트 한 줄(짧은 CTA)만 지운다. 본문 문단에 섞인 링크는 그대로 둔다.
 */
export const stripHandoffHintLines = (content) => {
  const text = String(content ?? '');
  if (!text) return text;
  const newline = text.includes('\r\n') ? '\r\n' : '\n';
  const lines = text.split(/\r?\n/);
  let removed = false;
  const kept = lines.filter((line) => {
    if (isHandoffHintLine(line)) {
      removed = true;
      return false;
    }
    return true;
  });
  if (!removed) return text;
  return kept.join(newline).replace(/(?:\r?\n){3,}/g, `${newline}${newline}`).replace(/^(?:\r?\n)+|(?:\r?\n)+$/g, '');
};

export const buildHandoffSuggestion = ({
  href,
  messageId,
  targetCategory,
} = {}) => {
  const resolvedTarget = targetCategory || isSuggestionHandoffHref(href);
  const copy = HANDOFF_SUGGESTION_COPY[resolvedTarget];
  if (!copy || !resolvedTarget || !href) return null;
  return {
    id: `handoff:${resolvedTarget}:${messageId || ''}`,
    type: 'category_handoff',
    title: copy.title,
    // 새 탭에서 열린다 (ChatPage.handleChatHandoffLink) — 카드가 아이콘으로 표시한다.
    opensInNewTab: true,
    primary: { label: copy.primaryLabel, action: 'open_handoff' },
    secondary: { label: '괜찮아요', action: 'dismiss_handoff' },
    payload: { href, targetCategory: resolvedTarget },
  };
};

export const createChatHandoffPayload = ({
  editSuggestions,
  expiresAt,
  href,
  referenceImageUrl,
  referenceMode,
  referenceTitle,
  referenceTypeLabel,
  sourceContent,
  sourceCategory,
  sourceConversationId,
  sourceDocFilename,
  sourceDocId,
  sourceDocTitle,
  sourcePurpose,
}) => {
  const targetCategory = getChatHandoffTarget(href);
  const context = normalizeContext(sourceContent);
  if (!targetCategory || !context) return null;
  const target = CHAT_HANDOFF_TARGETS[targetCategory];
  const normalizedReferenceImageUrl = normalizeReferenceImageUrl(referenceImageUrl);
  const sourceDocFields = normalizeSourceDocFields({
    editSuggestions,
    sourceDocFilename,
    sourceDocId,
    sourceDocTitle,
  });
  return {
    // v3: sourceDoc*(방금 만든 HWPX 첨부) 추가. 소비 측은 v1/v2 도 그대로 받는다.
    schemaVersion: sourceDocFields.sourceDocId ? 3 : 2,
    targetCategory,
    sourceCategory: String(sourceCategory || '').slice(0, 40),
    sourceConversationId: String(sourceConversationId || '').slice(0, 160),
    sourcePurpose: normalizeLabel(sourcePurpose, 40),
    createdAt: Date.now(),
    ...(Number(expiresAt) > Date.now() && {
      expiresAt: Math.min(
        Number(expiresAt),
        Date.now() + MAX_HANDOFF_TTL_MS,
      ),
    }),
    prompt: `${
      targetCategory === 'document'
      && sourcePurpose === CHAT_HANDOFF_FORM_FILL_PURPOSE
      && !sourceDocFields.sourceDocId
        ? target.formInstruction
        : target.instruction
    }\n\n[채팅에서 전달된 내용]\n${context}`,
    ...(normalizedReferenceImageUrl && {
      referenceImageUrl: normalizedReferenceImageUrl,
      referenceMode: referenceMode === 'all_pages' ? 'all_pages' : 'first_page',
      referenceTitle: normalizeLabel(referenceTitle, 120) || '참고 이미지',
      referenceTypeLabel: normalizeLabel(referenceTypeLabel, 40) || '이미지',
    }),
    ...sourceDocFields,
  };
};

const createHandoffId = () => {
  if (typeof window !== 'undefined' && window.crypto?.randomUUID) {
    return window.crypto.randomUUID();
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
};

const getSessionStorage = () => {
  try {
    return typeof window !== 'undefined' ? window.sessionStorage : null;
  } catch (_) {
    return null;
  }
};

/**
 * 이어가기 payload 저장소.
 * 제안 카드는 새 탭(noopener)으로 열리므로 탭별 sessionStorage 는 새 탭에 복사되지 않는다.
 * 같은 오리진의 모든 탭이 보는 localStorage 를 기본으로 쓰고, 막혀 있으면 sessionStorage 로 내려간다.
 * 일회성 소비(consume 시 삭제)와 TTL 검증은 그대로이므로 저장소가 바뀌어도 payload 수명은 같다.
 */
const getHandoffStorage = () => {
  try {
    if (typeof window !== 'undefined' && window.localStorage) return window.localStorage;
  } catch (_) {
    // localStorage 접근 차단(사생활 보호 모드 등) → sessionStorage 폴백
  }
  return getSessionStorage();
};

const isHandoffPayloadExpired = (raw, now) => {
  try {
    const payload = JSON.parse(raw);
    const createdAt = Number(payload?.createdAt || 0);
    const expiresAt = Number(payload?.expiresAt || 0) || (createdAt + HANDOFF_TTL_MS);
    return !createdAt || now > expiresAt || now > createdAt + MAX_HANDOFF_TTL_MS;
  } catch (_) {
    return true;
  }
};

/**
 * 소비되지 않고 남은 만료 payload 를 지운다(새 탭을 닫아버린 경우 등).
 * localStorage 는 탭이 닫혀도 남으므로 저장할 때마다 한 번 청소한다. best-effort.
 */
export const sweepExpiredChatHandoffs = (storage = getHandoffStorage(), now = Date.now()) => {
  if (!storage || typeof storage.key !== 'function' || typeof storage.length !== 'number') return 0;
  const staleKeys = [];
  try {
    for (let index = 0; index < storage.length; index += 1) {
      const key = storage.key(index);
      if (!key || !key.startsWith(STORAGE_PREFIX)) continue;
      if (isHandoffPayloadExpired(storage.getItem(key), now)) staleKeys.push(key);
    }
    staleKeys.forEach((key) => storage.removeItem(key));
  } catch (_) {
    // 저장소 접근 실패는 무시 — 다음 저장 때 다시 시도한다.
  }
  return staleKeys.length;
};

let memoryClassPhotoActivation = null;

const normalizeActivationRecord = (record, now = Date.now()) => {
  const activationId = String(record?.activationId || '');
  const targetCategory = String(record?.targetCategory || '');
  const createdAt = Number(record?.createdAt || 0);
  const expiresAt = Number(record?.expiresAt || 0);
  if (
    !/^[a-z0-9_-]{8,160}$/i.test(activationId)
    || !CHAT_HANDOFF_TARGETS[targetCategory]
    || !Number.isFinite(createdAt)
    || !Number.isFinite(expiresAt)
    || createdAt > now + 60_000
    || expiresAt <= now
    || expiresAt > createdAt + MAX_HANDOFF_TTL_MS
  ) {
    return null;
  }
  return {
    schemaVersion: 1,
    activationId,
    targetCategory,
    createdAt,
    expiresAt,
  };
};

export const clearClassPhotoActivation = (
  storage = getSessionStorage(),
) => {
  memoryClassPhotoActivation = null;
  try {
    storage?.removeItem(CLASS_PHOTO_ACTIVATION_KEY);
  } catch (_) {
    // sessionStorage가 차단되면 현재 SPA 수명 동안의 메모리 상태만 지운다.
  }
};

export const readClassPhotoActivation = (
  storage = getSessionStorage(),
  now = Date.now(),
) => {
  let candidate = memoryClassPhotoActivation;
  try {
    const raw = storage?.getItem(CLASS_PHOTO_ACTIVATION_KEY);
    if (raw) candidate = JSON.parse(raw);
  } catch (_) {
    // 저장소 접근이 막힌 환경에서는 메모리 fallback을 사용한다.
  }
  const normalized = normalizeActivationRecord(candidate, now);
  if (!normalized) {
    clearClassPhotoActivation(storage);
    return null;
  }
  return normalized;
};

export const armClassPhotoActivation = ({
  activationId,
  expiresAt,
  targetCategory,
}, storage = getSessionStorage(), now = Date.now()) => {
  const record = normalizeActivationRecord({
    activationId: activationId || createHandoffId(),
    targetCategory,
    createdAt: now,
    expiresAt: Math.min(
      Number(expiresAt) > now
        ? Number(expiresAt)
        : now + MAX_HANDOFF_TTL_MS,
      now + MAX_HANDOFF_TTL_MS,
    ),
  }, now);
  if (!record) return null;
  memoryClassPhotoActivation = record;
  try {
    storage?.setItem(CLASS_PHOTO_ACTIVATION_KEY, JSON.stringify(record));
  } catch (_) {
    // sessionStorage가 차단되어도 현재 SPA에서는 one-shot 귀속을 유지한다.
  }
  return record;
};

export const consumeClassPhotoActivation = (
  targetCategory,
  storage = getSessionStorage(),
  now = Date.now(),
) => {
  const record = readClassPhotoActivation(storage, now);
  if (!record) return null;
  clearClassPhotoActivation(storage);
  return record.targetCategory === targetCategory ? record : null;
};

export const storeChatHandoff = (payload, storage = getHandoffStorage()) => {
  if (!payload || !storage) return null;
  sweepExpiredChatHandoffs(storage);
  const id = createHandoffId();
  try {
    storage.setItem(`${STORAGE_PREFIX}${id}`, JSON.stringify(payload));
    return id;
  } catch (_) {
    return null;
  }
};

export const consumeChatHandoff = (
  id,
  storage = getHandoffStorage(),
  now = Date.now()
) => {
  if (!/^[a-z0-9-]{8,80}$/i.test(String(id || '')) || !storage) return null;
  const key = `${STORAGE_PREFIX}${id}`;
  let raw = null;
  try {
    raw = storage.getItem(key);
    storage.removeItem(key);
    // 배포 전환기: 옛 프론트가 sessionStorage 에 저장한 payload 도 같은 탭에서는 읽어준다.
    if (raw === null && storage !== getSessionStorage()) {
      const legacy = getSessionStorage();
      raw = legacy?.getItem(key) ?? null;
      legacy?.removeItem(key);
    }
  } catch (_) {
    return null;
  }
  if (!raw) return null;
  try {
    const payload = JSON.parse(raw);
    if (
      ![1, 2, 3].includes(payload?.schemaVersion)
      || !CHAT_HANDOFF_TARGETS[payload.targetCategory]
      || typeof payload.prompt !== 'string'
      || payload.prompt.length > MAX_CONTEXT_LENGTH + 300
      || now > Number(
        payload.expiresAt
        || (Number(payload.createdAt || 0) + HANDOFF_TTL_MS)
      )
      || Number(payload.expiresAt || 0)
        > Number(payload.createdAt || 0) + MAX_HANDOFF_TTL_MS
      || Number(payload.createdAt || 0) > now + 60_000
    ) {
      return null;
    }
    if (payload.referenceImageUrl) {
      const normalizedReferenceImageUrl = normalizeReferenceImageUrl(payload.referenceImageUrl);
      if (!normalizedReferenceImageUrl) return null;
      payload.referenceImageUrl = normalizedReferenceImageUrl;
      payload.referenceMode = payload.referenceMode === 'all_pages' ? 'all_pages' : 'first_page';
      payload.referenceTitle = normalizeLabel(payload.referenceTitle, 120) || '참고 이미지';
      payload.referenceTypeLabel = normalizeLabel(payload.referenceTypeLabel, 40) || '이미지';
    }
    // v3 첨부 메타는 저장소가 변조돼도 형식이 맞는 값만 통과시킨다.
    const sourceDocFields = normalizeSourceDocFields(payload);
    delete payload.sourceDocId;
    delete payload.sourceDocTitle;
    delete payload.sourceDocFilename;
    delete payload.editSuggestions;
    Object.assign(payload, sourceDocFields);
    return payload;
  } catch (_) {
    return null;
  }
};

export const buildChatHandoffUrl = (targetCategory, handoffId) => {
  if (!CHAT_HANDOFF_TARGETS[targetCategory] || !handoffId) return '/chat';
  const params = new URLSearchParams({
    category: targetCategory,
    handoff: handoffId,
  });
  return `/chat?${params.toString()}`;
};
